'use client';
import classNames from 'classnames/bind';
import React, { useContext, useEffect, createContext, useRef, useState, useMemo } from 'react';
import { useIsomorphicLayoutEffect } from '@financial/common-hooks';
import useApplyLogging from '../../hooks/useApplyLogging.mjs';
import useHeightOfElement from '../../hooks/useHeightOfElement.mjs';
import useScrollY from '../../hooks/useScrollY.mjs';
import { styleProperties } from '../../utils/ref.mjs';
import styles from './StickyHeaderContentArea.module.scss.mjs';

const cx = classNames.bind(styles);
const FIXED_CONTENT_AREA_CLASSNAME = cx('fixed-content-area');
const createFixedStyle = function () {
  let headerHeight = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 0;
  let {
    animated
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {
    animated: false
  };
  return {
    top: `${headerHeight}px`,
    transform: `translateY(${animated ? `${-headerHeight}px` : 0})`
  };
};
const StickyHeaderContentAreaContext = /*#__PURE__*/createContext(null);
function StickyHeaderContentAreaProvider(_ref) {
  let {
    children,
    hideHeaderOnScroll,
    isFixedHeader,
    initialHeightForSsr
  } = _ref;
  const headerRef = useRef(null);
  const height = useHeightOfElement(headerRef, {
    initialHeightForSsr
  });
  const contentAreaRef = useRef(null);
  const contentWrapperRef = useRef(null);
  const {
    direction
  } = useScrollY();
  const [contentAreaTop, setContentAreaTop] = useState();
  const contentAreaHeightRef = useRef(0);
  useIsomorphicLayoutEffect(() => {
    const onScroll = () => {
      if (contentWrapperRef.current) {
        const {
          top,
          height: _contentAreaHeight
        } = contentWrapperRef.current.getBoundingClientRect();
        setContentAreaTop(top);
        if (contentAreaHeightRef.current === 0) {
          contentAreaHeightRef.current = _contentAreaHeight;
        }
      }
    };
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const headerStatus = useMemo(() => ({
    HIDDEN_OR_FIXED: hideHeaderOnScroll || isFixedHeader,
    FIXED: isFixedHeader && !hideHeaderOnScroll,
    HIDDEN: hideHeaderOnScroll && !isFixedHeader,
    DYNAMIC: !hideHeaderOnScroll && !isFixedHeader,
    STATIC: isFixedHeader && hideHeaderOnScroll
  }), [isFixedHeader, hideHeaderOnScroll]);
  /** (예외) STATIC 헤더 : 역스크롤 시 contentArea의 높이와 contentArea의 top이 같아졌을 때 inView 상태로 간주  */
  const staticBaseY = headerStatus.STATIC && direction === 'DOWN' ? contentAreaHeightRef.current : 0;
  const isStaticInView = staticBaseY > 0 && !!contentAreaTop && contentAreaTop >= staticBaseY;
  const baseY = isFixedHeader ? height : 0;
  const isInView = isStaticInView ? isStaticInView : !!contentAreaTop && contentAreaTop > baseY;
  const isScrollDown = !isInView && direction === 'DOWN';
  const isFixedToTopWithStaticHeader = !isInView && headerStatus.STATIC;
  const childrenPaddingStyle = useMemo(() => {
    if (!isFixedHeader) {
      return {};
    }
    if (!isFixedToTopWithStaticHeader) {
      return {
        paddingTop: height
      };
    }
    return {
      paddingTop: isFixedToTopWithStaticHeader ? contentAreaHeightRef.current : 0,
      ...(isFixedToTopWithStaticHeader && {
        transition: 'padding-top 0.3s'
      })
    };
  }, [isFixedHeader, height, isFixedToTopWithStaticHeader]);
  return /*#__PURE__*/React.createElement(StickyHeaderContentAreaContext.Provider, {
    value: {
      isInView,
      contentWrapperRef,
      contentAreaRef,
      headerRef,
      isScrollDown,
      height,
      headerStatus
    }
  }, /*#__PURE__*/React.createElement("div", {
    style: childrenPaddingStyle
  }, children));
}
const useStickyHeaderContentArea = () => {
  const context = useContext(StickyHeaderContentAreaContext);
  if (!context) {
    throw new Error('[deFign/StickyHeaderContentArea] Could not use StickyHeaderContentAreaContext. You should use hooks in the StickyHeaderContentArea.Fixed.');
  }
  const {
    isInView
  } = context;
  /**
   * @description
   * StickyHeaderContentArea에서 외부로 제공할 값은 여기에 추가해주세요.
   */
  return {
    isFixed: !isInView
  };
};
function TopHeader(_ref2) {
  let {
    header,
    hideHeaderOnScroll = false,
    isFixedHeader = true
  } = _ref2;
  const context = useContext(StickyHeaderContentAreaContext);
  if (context === null) {
    throw new Error('StickyHeaderContentAreaContext Not Found. Inner logic is wrong.');
  }
  const {
    isInView,
    headerRef,
    isScrollDown,
    height
  } = context;
  const {
    position
  } = useScrollY();
  const shouldShowHeader = !hideHeaderOnScroll && !isInView;
  return (
    /*#__PURE__*/
    /** 상단 Header */
    React.createElement("div", {
      ref: headerRef,
      className: cx({
        article: shouldShowHeader || isFixedHeader
      })
    }, /*#__PURE__*/React.createElement("div", {
      className: cx('inner'),
      style: hideHeaderOnScroll ? {
        transform: `translateY(${isFixedHeader && !isInView ? -height : 0}px)`
      } : {
        transform: isFixedHeader || isScrollDown || position < height ? 'translateY(0)' : `translateY(${-height}px)`
      }
    }, header))
  );
}
/**
 * 고정영역이 컨텐츠에 포함된 StickyHeaderArea
 *
 * ### Usage
 * ```jsx
 * <StickyHeaderContentArea header={<HeaderBrandMain {...headerProps} />}>
 *     <div>상단 컨텐츠</div>
 *     <StickyHeaderContentArea.Fixed>
 *         고정 영역
 *     </StickyHeaderContentArea.Fixed>
 * </StickyHeaderContentArea>
 * ```
 */
function StickyHeaderContentArea(_ref3) {
  let {
    header,
    children,
    hideHeaderOnScroll = false,
    isFixedHeader = true,
    initialHeightForSsr
  } = _ref3;
  useApplyLogging('StickyHeaderContentArea');
  return /*#__PURE__*/React.createElement(StickyHeaderContentAreaProvider, {
    hideHeaderOnScroll: hideHeaderOnScroll,
    isFixedHeader: isFixedHeader,
    initialHeightForSsr: initialHeightForSsr
  }, /*#__PURE__*/React.createElement(TopHeader, {
    header: header,
    hideHeaderOnScroll: hideHeaderOnScroll,
    isFixedHeader: isFixedHeader
  }), children);
}
StickyHeaderContentArea.displayName = 'StickyHeaderContentArea';
StickyHeaderContentArea.Fixed = _ref4 => {
  let {
    children,
    ...refProps
  } = _ref4;
  const context = useContext(StickyHeaderContentAreaContext);
  if (context === null) {
    throw new Error('StickyHeaderContentAreaContext Not Found. Inner logic is wrong.');
  }
  const {
    contentWrapperRef,
    contentAreaRef,
    isInView,
    isScrollDown,
    height,
    headerStatus
  } = context;
  useEffect(() => {
    if (!contentAreaRef.current) {
      throw new Error('Not Found contentAreaRef.');
    }
    const contentStyleProperties = styleProperties(contentAreaRef.current);
    // 역스크롤 시 헤더 보일 수 있도록 contentRef 내리기
    if (isScrollDown && headerStatus.DYNAMIC) {
      contentStyleProperties.set({
        transform: `translateY(${height}px)`
      });
      return;
    }
    // static 상태에서는 상단 고정 애니메이션 스타일 적용
    if (headerStatus.STATIC) {
      const contentAreaAnimatedFixedStyle = createFixedStyle(height, {
        animated: true
      });
      if (!isInView) {
        contentStyleProperties.set(contentAreaAnimatedFixedStyle);
        contentStyleProperties.setClass(FIXED_CONTENT_AREA_CLASSNAME);
      } else {
        contentStyleProperties.remove(contentAreaAnimatedFixedStyle);
        contentStyleProperties.removeClass(FIXED_CONTENT_AREA_CLASSNAME);
      }
      return;
    }
    const contentParentStyleProperties = styleProperties(contentWrapperRef.current);
    const contentAreaFixedStyle = createFixedStyle(headerStatus.FIXED ? height : 0);
    // 상단에 고정 시, header 보여야 하는 여부에 따라 헤더의 height을 더함
    if (!isInView) {
      var _contentAreaRef$curre, _contentAreaRef$curre2;
      contentParentStyleProperties.set({
        height: `${headerStatus.HIDDEN_OR_FIXED ? (_contentAreaRef$curre = contentAreaRef.current) === null || _contentAreaRef$curre === void 0 ? void 0 : _contentAreaRef$curre.offsetHeight : ((_contentAreaRef$curre2 = contentAreaRef.current) === null || _contentAreaRef$curre2 === void 0 ? void 0 : _contentAreaRef$curre2.offsetHeight) + height}px`
      });
      contentStyleProperties.set(contentAreaFixedStyle);
      contentStyleProperties.setClass(FIXED_CONTENT_AREA_CLASSNAME);
    }
    // 이외에는 상단 고정 스타일을 제거
    else {
      contentParentStyleProperties.remove({
        height
      });
      contentStyleProperties.remove(contentAreaFixedStyle);
      contentStyleProperties.removeClass(FIXED_CONTENT_AREA_CLASSNAME);
    }
  }, [isInView, isScrollDown, height, contentAreaRef, headerStatus, contentWrapperRef]);
  return /*#__PURE__*/React.createElement("div", {
    ref: contentWrapperRef
  }, /*#__PURE__*/React.createElement("div", {
    ref: contentAreaRef,
    ...refProps
  }, children));
};

export { StickyHeaderContentArea as default, useStickyHeaderContentArea };
